/**
 * State-driven Arcturian/Dr.-Tschauko avatar playback.
 *
 * The visual state is explicit. Spoken text is never scanned for emotion, and
 * thinking never borrows the concerned/sad clip. Two persistent video panels
 * implement the Web side of the manifest's first-frame/fallback contract.
 */

import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import {
  ARCTURIAN_AVATAR_MANIFEST,
  assetForRole,
  shouldPreemptAvatarGreeting,
  type ArcturianAvatarAsset,
  type ArcturianAvatarAssetRole,
  type ArcturianAvatarState,
} from './ArcturianAvatarManifest'
import { DualVideoCrossfader } from './DualVideoCrossfader'

const idleAsset = assetForRole('idle')

/** Compatibility export for the ambient avatar and older consumers. */
export const CIOCCO_CLIPS = {
  idle: idleAsset.url,
  talk: [
    assetForRole('talk_short').url,
    assetForRole('talk_medium').url,
    assetForRole('talk_long').url,
  ],
  happy: assetForRole('happy').url,
  joyful: assetForRole('joyful').url,
  sad: assetForRole('sad').url,
  greeting: assetForRole('greeting').url,
} as const

/** Neutral full-resolution poster used only before the first frame or on total failure. */
export const TSCHAUKO_POSTER_URL = idleAsset.poster

/** Small poster for the 64 px ambient avatar. */
export const TSCHAUKO_POSTER_THUMB_URL =
  `${idleAsset.url.split('?')[0]}?variant=thumbnail&format=jpg&v=${idleAsset.sha256}`

function roleForState(
  state: ArcturianAvatarState,
  speakingRole: ArcturianAvatarAssetRole,
  pleasedRole: ArcturianAvatarAssetRole,
): ArcturianAvatarAssetRole | null {
  switch (state) {
    case 'speaking':
      return speakingRole
    case 'pleased':
      return pleasedRole
    case 'concerned':
      return 'sad'
    case 'greeting':
      return 'greeting'
    case 'error':
      return null
    case 'idle':
    case 'listening':
    case 'thinking':
      return 'idle'
  }
}

/**
 * Drives the two Web video panels from an explicit presentation state.
 *
 * Speaking clips rotate per turn; they are not selected from predicted text
 * duration. Pleased alternates between the two verified positive clips.
 */
export function useTschaukoVideo(
  videoRefs: readonly [RefObject<HTMLVideoElement | null>, RefObject<HTMLVideoElement | null>],
  state: ArcturianAvatarState,
  sessionActive: boolean,
  failed: boolean,
  setFailed: (value: boolean) => void,
): void {
  const [videoRefA, videoRefB] = videoRefs
  const crossfader = useRef<DualVideoCrossfader | null>(null)
  const speakingRole = useRef<ArcturianAvatarAssetRole>('talk_medium')
  const pleasedRole = useRef<ArcturianAvatarAssetRole>('happy')
  const speakingRotation = useRef(0)
  const pleasedRotation = useRef(0)
  const previousState = useRef<ArcturianAvatarState | null>(null)
  const greeted = useRef(false)
  const greetingPlaying = useRef(false)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const videoA = videoRefA.current
    const videoB = videoRefB.current
    if (!videoA || !videoB) return
    const player = new DualVideoCrossfader([videoA, videoB], {
      fadeMs: ARCTURIAN_AVATAR_MANIFEST.renderer_contract.web.crossfade_ms,
      onLoadError: (fatal, src) => {
        if (fatal) setFailed(true)
        else console.warn('[ArcturianAvatar] Incoming clip failed; keeping active panel visible:', src)
      },
    })
    crossfader.current = player
    return () => {
      player.destroy()
      if (crossfader.current === player) crossfader.current = null
    }
  }, [videoRefA, videoRefB, setFailed])

  useEffect(() => {
    const player = crossfader.current
    if (!player) return
    if (failed || !sessionActive) {
      player.stop()
      greeted.current = false
      greetingPlaying.current = false
      previousState.current = null
      return
    }

    if (!greeted.current) {
      const greeting = assetForRole('greeting')
      greeted.current = true
      greetingPlaying.current = true
      player.setTarget({
        src: greeting.url,
        loop: greeting.loop,
        onEnded: () => {
          greetingPlaying.current = false
          setTick((value) => value + 1)
        },
      })
      return
    }
    if (greetingPlaying.current) {
      if (!shouldPreemptAvatarGreeting(state)) return
      greetingPlaying.current = false
    }

    if (state === 'speaking' && previousState.current !== 'speaking') {
      const roles = ARCTURIAN_AVATAR_MANIFEST.states.speaking.assets
      speakingRole.current = roles[speakingRotation.current % roles.length]
      speakingRotation.current += 1
    }
    if (state === 'pleased' && previousState.current !== 'pleased') {
      const roles = ARCTURIAN_AVATAR_MANIFEST.states.pleased.assets
      pleasedRole.current = roles[pleasedRotation.current % roles.length]
      pleasedRotation.current += 1
    }
    previousState.current = state

    const role = roleForState(state, speakingRole.current, pleasedRole.current)
    if (!role) {
      player.stop()
      return
    }
    const asset: ArcturianAvatarAsset = assetForRole(role)
    player.setTarget({ src: asset.url, loop: asset.loop })
  }, [state, sessionActive, failed, tick])
}
